import { inject, Injectable, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ɵSharedStylesHost } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class CustomStyleHost extends ɵSharedStylesHost implements OnDestroy {

  private readonly _doc = inject(DOCUMENT);
  private _pendingStyles: string[] = [];
  private _styleElements = new Map<string, HTMLStyleElement>();
  private _styleRefCounts = new Map<string, number>();
  private _flushInterval: ReturnType<typeof setInterval> | null = null;

  override addStyles(styles: string[]): void {
    const targets = this._getShadowRoots();

    for (const style of styles) {
      this._styleRefCounts.set(style, (this._styleRefCounts.get(style) ?? 0) + 1);
    }

    if (targets.length === 0) {
      for (const style of styles) {
        if (!this._pendingStyles.includes(style)) {
          this._pendingStyles.push(style);
        }
      }
      this._scheduleFlush();
      return;
    }

    this._cancelFlush();
    for (const target of targets) {
      this._flushPending(target);
      this._applyStyles(styles, target);
    }
  }

  override removeStyles(styles: string[]): void {
    for (const style of styles) {
      const currentCount = this._styleRefCounts.get(style) ?? 0;
      const newCount = currentCount - 1;

      if (newCount <= 0) {
        // No more components need this style — remove from DOM
        this._styleRefCounts.delete(style);
        this._removeStyleFromDom(style);
        // Also remove from pending queue
        this._pendingStyles = this._pendingStyles.filter(s => s !== style);
      } else {
        // Other components still need this style — just decrement
        this._styleRefCounts.set(style, newCount);
      }
    }
  }

  private _applyStyles(styles: string[], target: ShadowRoot): void {
    for (const style of styles) {
      const key = this._styleKey(style, target);
      const existing = this._styleElements.get(key);

      // If element exists and is still in DOM — nothing to do
      if (existing && target.contains(existing)) {
        continue;
      }

      // If tracked but detached — clean up stale reference
      if (existing && !target.contains(existing)) {
        this._styleElements.delete(key);
      }

      const el = this._doc.createElement('style');
      el.textContent = style;
      target.appendChild(el);
      this._styleElements.set(key, el);
    }
  }

  private _removeStyleFromDom(style: string): void {
    const targets = this._getShadowRoots();

    for (const target of targets) {
      const key = this._styleKey(style, target);
      const el = this._styleElements.get(key);
      if (el?.parentNode) {
        el.parentNode.removeChild(el);
      }
      this._styleElements.delete(key);
    }
  }

  private _flushPending(target: ShadowRoot): void {
    if (this._pendingStyles.length === 0) return;
    // Only flush styles that still have active ref counts
    const stillNeeded = this._pendingStyles.filter(
      s => (this._styleRefCounts.get(s) ?? 0) > 0
    );
    this._applyStyles(stillNeeded, target);
    this._pendingStyles = [];
  }

  private _getShadowRoots(): ShadowRoot[] {
    const nodes = this._doc.querySelectorAll('dms-app');
    return Array.from(nodes)
      .map(el => el.shadowRoot)
      .filter((sr): sr is ShadowRoot => sr !== null);
  }

  private _styleKey(style: string, target: ShadowRoot): string {
    const nodes = Array.from(this._doc.querySelectorAll('dms-app'));
    const index = nodes.indexOf(target.host as HTMLElement);
    return `${index}::${style}`;
  }

  private _scheduleFlush(): void {
    if (this._flushInterval !== null) return;

    this._flushInterval = setInterval(() => {
      const targets = this._getShadowRoots();
      if (targets.length === 0) return;

      this._cancelFlush();
      for (const target of targets) {
        this._flushPending(target);
      }
    }, 50);
  }

  private _cancelFlush(): void {
    if (this._flushInterval !== null) {
      clearInterval(this._flushInterval);
      this._flushInterval = null;
    }
  }

  override ngOnDestroy(): void {
    this._cancelFlush();
    this._styleElements.forEach(el => el.parentNode?.removeChild(el));
    this._styleElements.clear();
    this._styleRefCounts.clear();
    this._pendingStyles = [];
  }
}